/// <reference types="vite/client" />
import { recordError, recordEvent } from './new-relic';

/**
 * Handles redirection safely to avoid ad blockers
 */
function performRedirect(redirectUrl: string) {
  const link = document.createElement('a');
  link.href = redirectUrl;
  link.rel = 'noopener';
  link.target = '_self';
  document.body.appendChild(link);
  link.click();
}

/**
 * Loads a tracking pixel and returns a promise that resolves when loaded
 */
function loadPixel(pixelHtml: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    container.style.display = 'none'; // Hide the pixel container
    container.innerHTML = pixelHtml;

    const img = container.querySelector('img');
    if (img) {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Pixel image failed to load'));
    } else {
      resolve(); // No image found, consider it successful
    }

    document.body.appendChild(container);
  });
}

/**
 * Handles pixel loading with proper error tracking (fire and forget)
 */
function loadPixelAsync(pixel: string, brand: string): void {
  const pixelStartTime = performance.now();

  loadPixel(pixel)
    .then(() => {
      const pixelDuration = performance.now() - pixelStartTime;
      recordEvent('pixel_loaded', {
        durationMs: Math.round(pixelDuration),
        brand,
      });
    })
    .catch((pixelError) => {
      console.warn('Pixel loading failed:', pixelError);
      recordEvent('pixel_load_failed', {
        error: pixelError instanceof Error ? pixelError.message : String(pixelError),
        brand,
      });
      recordError(pixelError instanceof Error ? pixelError : new Error(String(pixelError)), {
        brand,
        context: 'pixel_loading',
      });
    });
}

/**
 * Main tracking and redirection handler
 */
async function trackEvent() {
  try {
    const startTime = performance.now();
    const params = new URLSearchParams(window.location.search);

    // Set page-level attributes for New Relic
    const trackingParams = {
      adhid: params.get('adhid') ?? '',
      event: params.get('event') ?? '',
      mode: params.get('mode') ?? '',
      referrer: document.referrer ?? '',
    };
    params.set('referrer', document.referrer ?? '');
    // Record the tracking event initiation
    recordEvent('track_event_initiated', trackingParams);

    const response = await fetch(`/api/track?${params.toString()}`);

    if (!response.ok) {
      recordEvent('api_error', {
        status: response.status,
        statusText: response.statusText,
      });
      throw new Error(`Failed to fetch data from API: ${response.status}`);
    }

    const data = await response.json();
    const { redirectUrl, pixel, brand } = data;

    // Record API success with performance timing
    const apiDuration = performance.now() - startTime;
    recordEvent('track_event_success', {
      hasRedirect: !!redirectUrl,
      hasPixel: !!pixel,
      brand,
      apiResponseTimeMs: Math.round(apiDuration),
    });

    // Start pixel loading in parallel (don't await - fire and forget)
    if (pixel) {
      loadPixelAsync(pixel, brand);
    }

    if (redirectUrl) {
      recordEvent('redirect_initiated', {
        url: redirectUrl,
      });

      // Wait 200ms to give pixel a chance to fire before redirecting
      setTimeout(() => {
        performRedirect(redirectUrl);
      }, 200);
    }
  } catch (error) {
    console.error('trackEvent failed:', error);
    if (error instanceof Error) {
      recordError(new Error(`trackEvent failed: ${error.message}`), {
        stack: error.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    } else {
      recordError(new Error(`trackEvent failed: ${JSON.stringify(error)}`), {
        url: window.location.href,
        userAgent: navigator.userAgent,
      });
    }
  }
}

// Add global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason);
  recordError(
    new Error(
      `Unhandled promise rejection: ${event.reason instanceof Error ? event.reason.message : String(event.reason)}`
    ),
    {
      type: 'unhandledrejection',
      url: window.location.href,
      stack: event.reason instanceof Error ? event.reason.stack : undefined,
    }
  );
});

trackEvent();
